import type { CSSProperties } from "react";
import type { VisualMode } from "../styles/themePalette";

/** Same shape as server `AvatarWire` in auth/contacts payloads. */
export type AvatarUrls = {
  original_url: string | null;
  terminal_url: string | null;
};

export function pickAvatarDisplayUrl(visualMode: VisualMode, urls: AvatarUrls | null | undefined): string | null {
  if (!urls) return null;
  if (visualMode === "modern") {
    return urls.original_url ?? urls.terminal_url;
  }
  return urls.terminal_url ?? urls.original_url;
}

/** Same wire shape as contacts / workspace members for URL resolution. */
export type AvatarWirePeer = {
  avatar_url?: string | null;
  avatar?: AvatarUrls | null;
};

/**
 * Primary URL matches `pickAvatarDisplayUrl`; `fallback` is the other variant when both exist
 * (so the UI can recover if the preferred file 404s, e.g. DB/disk mismatch in modern mode).
 */
export function avatarUrlsPrimaryFallback(
  visualMode: VisualMode,
  urls: AvatarUrls | null | undefined,
): { primary: string | null; fallback: string | null } {
  if (!urls) return { primary: null, fallback: null };
  const primary = pickAvatarDisplayUrl(visualMode, urls);
  const o = urls.original_url;
  const t = urls.terminal_url;
  if (o && t && o !== t) {
    if (primary === o) return { primary: o, fallback: t };
    if (primary === t) return { primary: t, fallback: o };
  }
  return { primary, fallback: null };
}

export function peerAvatarImageSources(visualMode: VisualMode, peer: AvatarWirePeer): {
  primary: string | null;
  fallback: string | null;
} {
  const urls = avatarUrlsFromWire(peer.avatar ?? undefined, peer.avatar_url ?? undefined);
  return avatarUrlsPrimaryFallback(visualMode, urls);
}

/** Normalize server payloads that may omit `avatar` for older clients. */
export function avatarUrlsFromWire(
  avatar: AvatarUrls | null | undefined,
  avatar_url: string | null | undefined,
): AvatarUrls | null {
  if (avatar && (avatar.original_url != null || avatar.terminal_url != null)) {
    return {
      original_url: avatar.original_url ?? null,
      terminal_url: avatar.terminal_url ?? null,
    };
  }
  if (typeof avatar_url === "string" && avatar_url.length > 0) {
    return { original_url: avatar_url, terminal_url: avatar_url };
  }
  return null;
}

/** Resolve display URL for a contact or workspace member row from the wire. */
export function avatarDisplayUrlForPeer(visualMode: VisualMode, peer: AvatarWirePeer): string | null {
  return peerAvatarImageSources(visualMode, peer).primary;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = hex.trim().replace(/^#/, "");
  if (m.length === 6 && /^[0-9a-f]+$/i.test(m)) {
    const n = parseInt(m, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  return null;
}

function rgbToHue(r: number, g: number, b: number): number {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  if (max === min) return 120;
  const d = max - min;
  let h = 0;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return Math.round(h * 360);
}

/** Approximate accent tint for neutral grayscale terminal avatars (CSS filter). */
export function avatarTerminalTintStyle(accentHex: string): CSSProperties {
  const rgb = hexToRgb(accentHex);
  const hue = rgb ? rgbToHue(rgb.r, rgb.g, rgb.b) : 120;
  return {
    filter: `sepia(0.4) saturate(1.65) hue-rotate(${hue - 95}deg) contrast(1.04)`,
  };
}
